import { ParsedData } from '../types';
import { getEntityColorClass } from './fileParser';

export interface EntityLabelInfo {
  label: string;
  count: number;
  colorClass: string;
}

export interface ModelEntityLabels {
  modelName: string;
  labels: EntityLabelInfo[];
  totalEntities: number;
}

export function extractEntityLabels(data: ParsedData): ModelEntityLabels[] {
  const modelLabelsMap = new Map<string, Map<string, number>>();
  
  // Initialize maps for each model
  data.modelNames.forEach(modelName => {
    modelLabelsMap.set(modelName, new Map<string, number>());
  });
  // Add a synthetic model bucket for human annotations
  const HUMAN_MODEL = 'Annotations';
  modelLabelsMap.set(HUMAN_MODEL, new Map<string, number>());
  
  // Count labels per model
  data.questions.forEach(question => {
    question.modelResponses.forEach(response => {
      const labelMap = modelLabelsMap.get(response.modelName);
      if (labelMap) {
        response.entities.forEach(entity => {
          const currentCount = labelMap.get(entity.label) || 0;
          labelMap.set(entity.label, currentCount + 1);
        });
      }
    });
    // Count labels for human annotations
    if (question.humanAnnotations && question.humanAnnotations.length > 0) {
      const humanMap = modelLabelsMap.get(HUMAN_MODEL)!;
      question.humanAnnotations.forEach(entity => {
        const currentCount = humanMap.get(entity.label) || 0;
        humanMap.set(entity.label, currentCount + 1);
      });
    }
  });
  
  // Add any custom labels that aren't in use yet (with count 0)
  if (data.customLabelColors) {
    Object.keys(data.customLabelColors).forEach(label => {
      modelLabelsMap.forEach((labelMap) => {
        if (!labelMap.has(label)) {
          labelMap.set(label, 0);
        }
      });
    });
  }
  
  // Convert to array format and sort by count
  const result: ModelEntityLabels[] = [];
  
  modelLabelsMap.forEach((labelMap, modelName) => {
    const labels: EntityLabelInfo[] = Array.from(labelMap.entries())
      .map(([label, count]) => ({
        label,
        count,
        colorClass: getEntityColorClass(label, data.customLabelColors)
      }))
      .sort((a, b) => {
        // Sort: used labels first (by count desc), then unused labels (alphabetically)
        if (a.count === 0 && b.count === 0) {
          return a.label.localeCompare(b.label);
        }
        return b.count - a.count;
      });
    
    const totalEntities = labels.reduce((sum, l) => sum + l.count, 0);
    
    result.push({
      modelName,
      labels,
      totalEntities
    });
  });
  
  return result;
}

export function getAllUniqueLabels(data: ParsedData): EntityLabelInfo[] {
  const labelMap = new Map<string, number>();
  
  data.questions.forEach(question => {
    question.modelResponses.forEach(response => {
      response.entities.forEach(entity => {
        const currentCount = labelMap.get(entity.label) || 0;
        labelMap.set(entity.label, currentCount + 1);
      });
    });
    // Include human annotations
    question.humanAnnotations?.forEach(entity => {
      const currentCount = labelMap.get(entity.label) || 0;
      labelMap.set(entity.label, currentCount + 1);
    });
  });
  
  // Add any custom labels that aren't in use yet (with count 0)
  if (data.customLabelColors) {
    Object.keys(data.customLabelColors).forEach(label => {
      if (!labelMap.has(label)) {
        labelMap.set(label, 0);
      }
    });
  }
  
  return Array.from(labelMap.entries())
    .map(([label, count]) => ({
      label,
      count,
      colorClass: getEntityColorClass(label, data.customLabelColors)
    }))
    .sort((a, b) => {
      // Sort: used labels first (by count desc), then unused labels (alphabetically)
      if (a.count === 0 && b.count === 0) {
        return a.label.localeCompare(b.label);
      }
      return b.count - a.count;
    });
}
